/**
 * Human-readable Czech labels for audit log action and entity type values.
 *
 * Keep in sync with internal/auth/actions.go.
 * When adding a new Action or EntityType constant there, add the label here.
 * ENTITY_TYPE_OPTIONS is derived automatically — no manual update needed.
 */

export const ACTION_LABELS: Record<string, string> = {
  // Auth
  "auth.login.success": "Přihlášení",
  "auth.login.failure": "Neúspěšné přihlášení",
  "auth.logout": "Odhlášení",

  // User
  "user.created": "Vytvoření uživatele",
  "user.updated": "Úprava uživatele",
  "user.password.changed": "Změna hesla",
  "user.password.reset": "Reset hesla",

  // VoucherType
  "voucher_type.created": "Vytvoření typu voucheru",
  "voucher_type.updated": "Úprava typu voucheru",

  // MerchKind
  "merch_kind.created": "Vytvoření druhu zboží",
  "merch_kind.updated": "Úprava druhu zboží",

  // BarcodeBank
  "barcode_bank.generated": "Generování čárových kódů",
  "barcode_bank.imported": "Import čárových kódů",
  "barcode_bank.voided": "Zneplatnění čárových kódů",

  // Voucher
  "voucher.issue": "Vydání voucheru",
  "voucher.cancel": "Zrušení voucheru",
  "voucher.update": "Úprava voucheru",
  "voucher.extend": "Prodloužení voucheru",

  // Ride
  "voucher.ride.register": "Přihlášení na jízdu",
  "voucher.ride.cancel": "Zrušení jízdy",

  // Certificate
  "certificate.created": "Vytvoření certifikátu",
  "certificate.updated": "Úprava certifikátu",
  "certificate.payment_state.changed": "Změna stavu platby certifikátu",
  "certificate.cancelled": "Zrušení certifikátu",
  "certificate.refunded": "Vrácení peněz za certifikát",
  "certificate.refund.manual_intervention": "Ruční zásah při vracení platby",
  "certificate.deleted": "Smazání certifikátu",
  "certificate.pdf.generated": "Vygenerování PDF certifikátu",
  "certificate.pdf.regenerated": "Přegenerování PDF certifikátu",
  "certificate.email.sent": "Odeslání e-mailu s certifikátem",
  "certificate.email.resent": "Opětovné odeslání e-mailu certifikátu",
  "certificate.email.refused": "Letterman odmítl e-mail s certifikátem",
  "certificate.mark_paid": "Certifikát označen jako uhrazený",
  "certificate.validity.extended": "Prodloužení platnosti certifikátu",
  "certificate.items.added": "Doplnění položek certifikátu ze synchronizace e-shopu",

  // CertificateItem
  "certificate_item.redeemed": "Uplatnění položky certifikátu",
  "certificate_item.unredeemed": "Zrušení uplatnění položky certifikátu",
  "certificate_item.cancelled": "Zrušení položky certifikátu",

  // EshopProductMap
  "eshop_product_map.created": "Vytvoření mapování produktu e-shopu",
  "eshop_product_map.updated": "Úprava mapování produktu e-shopu",
  "eshop_product_map.deleted": "Smazání mapování produktu e-shopu",

  // Email outbox of the retired SMTP sender; kept for older audit entries.
  "email_outbox.sent": "Odeslaný e-mail",
  "email_outbox.failed": "Neúspěšný e-mail",
  "email_outbox.expired": "Vypršelý e-mail",
  "email_outbox.retried": "E-mail znovu zařazen do fronty",
  "email_outbox.cancelled": "E-mail zrušen",

  // Reports
  "report.exported": "Export reportu",
};

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  user: "Uživatel",
  session: "Relace",
  voucher_type: "Typ voucheru",
  merch_kind: "Druh zboží",
  barcode_bank: "Banka čárových kódů",
  voucher: "Voucher",
  ride: "Jízda",
  eshop_certificate: "Certifikát e-shopu",
  certificate: "Certifikát",
  certificate_item: "Položka certifikátu",
  eshop_product_map: "Mapování produktu e-shopu",
  email_outbox: "E-mail",
  report: "Report",
};

/** Options for the entity-type filter <Select>. Derived automatically. */
export const ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Všechny" },
  ...Object.entries(ENTITY_TYPE_LABELS)
    .sort((a, b) => a[1].localeCompare(b[1], "cs"))
    .map(([value, label]) => ({ value, label })),
];

export function labelAction(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function labelEntityType(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}
